// Section 16 norms: precedent marks. Each season every norm's `reinforcedBy` and `contradictedBy`
// hooks read the season's report entries (the rationing phase's `rationing-outcome` and legacy
// `shortage` entries, the crime phase's `justice` entries). A season with a reinforcing entry
// counts one use; a contrary choice resets the run; 4 uses in a row establish the norm (a `norm`
// entry, which the Ledger records with `announced`); an established norm is lost after 8 seasons
// without reinforcement. Run at the end of the season so justice entries count the same season.
import type { NormId, PhaseContext, ReportEntry } from '../../core/types';

/** Was the precedent public: the announced ration order, or a standing (not case-by-case) justice policy? */
function announcedBy(ctx: PhaseContext, entry: ReportEntry): boolean {
  switch (entry.kind) {
    case 'rationing-outcome':
      return entry.announced;
    case 'justice':
      return !ctx.state.policies.justice.askEachCase;
    default:
      return ctx.state.policies.announceRations;
  }
}

export function updateNorms(ctx: PhaseContext): void {
  const entries = ctx.report.current.entries;
  const turn = ctx.season.turn;
  for (const id of Object.keys(ctx.registry.norms).sort() as NormId[]) {
    const def = ctx.registry.norms[id];
    const norm = ctx.draft.norms[id];
    const use = entries.find((e) => def.reinforcedBy(ctx, e));
    if (use !== undefined) {
      norm.count += 1;
      norm.lastReinforcedTurn = turn;
      if (!norm.established && norm.count >= def.establishAfter) {
        norm.established = true;
        norm.since = turn;
        norm.announced = announcedBy(ctx, use);
        ctx.report.add({ kind: 'norm', normId: id, change: 'established' });
      }
      continue;
    }
    if (entries.some((e) => def.contradictedBy(ctx, e))) norm.count = 0;
    if (
      norm.established &&
      norm.lastReinforcedTurn !== null &&
      turn - norm.lastReinforcedTurn >= def.decayAfter
    ) {
      norm.established = false;
      norm.since = null;
      norm.count = 0;
      ctx.report.add({ kind: 'norm', normId: id, change: 'lost' });
    }
  }
}
